import { create } from 'zustand';

type ThemePreference = 'system' | 'light' | 'dark';
type ResolvedTheme = 'light' | 'dark';
type ReadFilter = '' | 'true' | 'false';

interface UIState {
  // Drawer state
  isDrawerCollapsed: boolean;
  toggleDrawer: () => void;
  setDrawerCollapsed: (collapsed: boolean) => void;

  // Theme state
  themePreference: ThemePreference;
  resolvedTheme: ResolvedTheme;
  setThemePreference: (pref: ThemePreference) => void;
  cycleTheme: () => void;
  initializeTheme: () => void;

  // Message filter state
  inboxReadFilter: ReadFilter;
  updatesReadFilter: ReadFilter;
  parentReadFilter: ReadFilter;
  setInboxReadFilter: (filter: ReadFilter) => void;
  setUpdatesReadFilter: (filter: ReadFilter) => void;
  setParentReadFilter: (filter: ReadFilter) => void;
}

const THEME_STORAGE_KEY = 'theme-preference';
const INBOX_READ_FILTER_KEY = 'inbox-read-filter';
const UPDATES_READ_FILTER_KEY = 'updates-read-filter';
const PARENT_READ_FILTER_KEY = 'parent-read-filter';

// Guard to prevent duplicate media query listeners
let themeListenerInitialized = false;

/**
 * Helper to safely access localStorage (avoids SSR crashes)
 */
const getStoredDrawerState = (): boolean => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('drawerCollapsed') === 'true';
};

/**
 * Get stored read filter from localStorage (defaults to 'false' = Unread)
 */
const getStoredReadFilter = (key: string): ReadFilter => {
  if (typeof window === 'undefined') return 'false';
  const stored = localStorage.getItem(key);
  if (stored === '' || stored === 'true' || stored === 'false') {
    return stored;
  }
  return 'false';
};

/**
 * Get stored parent read filter from localStorage (defaults to '' = All Messages)
 * Parents are less frequent users, so showing all messages is more helpful.
 */
const getStoredParentReadFilter = (): ReadFilter => {
  if (typeof window === 'undefined') return '';
  const stored = localStorage.getItem(PARENT_READ_FILTER_KEY);
  if (stored === '' || stored === 'true' || stored === 'false') {
    return stored;
  }
  return '';
};

/**
 * Get stored theme preference from localStorage
 */
const getStoredThemePreference = (): ThemePreference => {
  if (typeof window === 'undefined') return 'light';
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored;
  }
  return 'light'; // Default must match index.html anti-flash script default
};

/**
 * Get system preference using matchMedia
 */
const getSystemTheme = (): ResolvedTheme => {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

/**
 * Resolve theme based on preference
 */
const resolveTheme = (preference: ThemePreference): ResolvedTheme => {
  if (preference === 'system') {
    return getSystemTheme();
  }
  return preference;
};

/**
 * Apply theme to document
 */
const applyTheme = (theme: ResolvedTheme): void => {
  if (typeof document === 'undefined') return;
  if (theme === 'dark') {
    document.documentElement.setAttribute('data-theme', 'dark');
  } else {
    document.documentElement.removeAttribute('data-theme');
  }
};

/**
 * UI Store - manages global UI state
 *
 * Handles:
 * - Desktop navigation drawer collapse state (persisted to localStorage)
 * - Theme preference (system/light/dark) with OS preference detection
 */
export const useUIStore = create<UIState>((set, get) => ({
  // Drawer state
  isDrawerCollapsed: getStoredDrawerState(),

  toggleDrawer: () =>
    set((state) => {
      const newValue = !state.isDrawerCollapsed;
      localStorage.setItem('drawerCollapsed', String(newValue));
      return { isDrawerCollapsed: newValue };
    }),

  setDrawerCollapsed: (collapsed: boolean) => {
    localStorage.setItem('drawerCollapsed', String(collapsed));
    set({ isDrawerCollapsed: collapsed });
  },

  // Theme state
  themePreference: getStoredThemePreference(),
  resolvedTheme: resolveTheme(getStoredThemePreference()),

  setThemePreference: (pref: ThemePreference) => {
    localStorage.setItem(THEME_STORAGE_KEY, pref);
    const resolved = resolveTheme(pref);
    applyTheme(resolved);
    set({ themePreference: pref, resolvedTheme: resolved });
  },

  cycleTheme: () => {
    const current = get().themePreference;
    const next: ThemePreference = current === 'system' ? 'light' : current === 'light' ? 'dark' : 'system';
    get().setThemePreference(next);
  },

  // Message filter state
  inboxReadFilter: getStoredReadFilter(INBOX_READ_FILTER_KEY),
  updatesReadFilter: getStoredReadFilter(UPDATES_READ_FILTER_KEY),
  parentReadFilter: getStoredParentReadFilter(),

  setInboxReadFilter: (filter: ReadFilter) => {
    localStorage.setItem(INBOX_READ_FILTER_KEY, filter);
    set({ inboxReadFilter: filter });
  },

  setUpdatesReadFilter: (filter: ReadFilter) => {
    localStorage.setItem(UPDATES_READ_FILTER_KEY, filter);
    set({ updatesReadFilter: filter });
  },

  setParentReadFilter: (filter: ReadFilter) => {
    localStorage.setItem(PARENT_READ_FILTER_KEY, filter);
    set({ parentReadFilter: filter });
  },

  initializeTheme: () => {
    const pref = get().themePreference;
    const resolved = resolveTheme(pref);
    applyTheme(resolved);
    set({ resolvedTheme: resolved });

    // Listen for OS theme changes when preference is 'system'
    // Guard prevents duplicate listeners if called multiple times
    if (typeof window !== 'undefined' && !themeListenerInitialized) {
      themeListenerInitialized = true;
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => {
        if (get().themePreference === 'system') {
          const newResolved = getSystemTheme();
          applyTheme(newResolved);
          set({ resolvedTheme: newResolved });
        }
      };
      mediaQuery.addEventListener('change', handleChange);
    }
  },
}));
